import type { ReactNode } from 'react';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import usePlans from '@alltrails/context/hooks/usePlans';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import ModalAriaLabelledByHeading from '@alltrails/modal/components/Modal/ModalAriaLabelledByHeading';
import type SubscriptionUser from '@alltrails/types/SubscriptionUser';
import PeakFeatureList from '../PeakFeatureList/PeakFeatureList';
import PlanSelection from '../PlanSelection/PlanSelection';
import TierSubheading from './TierSubheading';
import styles from './UpsellContent.module.scss';

type Props = {
  heading: ReactNode;
  hidePeak?: boolean;
  /**
   * When true, the plus options will not be shown.
   * Note that if `hidePeak` is also true, this will be ignored.
   */
  hidePlus?: boolean;
  promoEligibleHeading: ReactNode;
  setTierSelected: (tier: PurchaseTier) => void;
  showPlusFirst?: boolean;
  subscriptionUser: SubscriptionUser | null;
  subheading?: ReactNode;
  tierSelected: PurchaseTier;
  upsellTrigger: UpsellTrigger;
};

const GenericUpsellContent = ({
  heading,
  hidePeak = false,
  hidePlus = false,
  promoEligibleHeading,
  setTierSelected,
  showPlusFirst,
  subscriptionUser,
  tierSelected,
  subheading,
  upsellTrigger
}: Props) => {
  const user = useUser();
  const plans = usePlans();
  const isPromoEligible = !user || user?.isPromoEligible;
  const hidePlusContent = hidePlus || user?.subscriptionTier === 'plus';

  return (
    <>
      {hidePlusContent && !hidePeak ? (
        <>
          <PeakLogoBadge size="md" variant="full" className={styles.logo} />
          <ModalAriaLabelledByHeading as="h4" size="400">
            {isPromoEligible ? promoEligibleHeading : heading}
          </ModalAriaLabelledByHeading>
          <TierSubheading tier="peak" />
        </>
      ) : hidePeak ? (
        <>
          <PlusLogoBadge size="md" variant="full" className={styles.logo} />
          <ModalAriaLabelledByHeading as="h4" size="400">
            {isPromoEligible ? promoEligibleHeading : heading}
          </ModalAriaLabelledByHeading>
          {subheading ? (
            <Typography component="div" variant="text200">
              {subheading}
            </Typography>
          ) : (
            <TierSubheading tier="plus" />
          )}
        </>
      ) : (
        <>
          <ModalAriaLabelledByHeading as="h4" size="400">
            {isPromoEligible ? promoEligibleHeading : heading}
          </ModalAriaLabelledByHeading>
          <PlanSelection
            isPromoEligible={isPromoEligible}
            plans={plans}
            tierSelected={tierSelected}
            setTierSelected={setTierSelected}
            showPlusFirst={showPlusFirst}
            hidePeak={hidePeak}
            trigger={upsellTrigger}
            subscriptionUser={subscriptionUser}
          />
        </>
      )}
      <PeakFeatureList tierSelected={tierSelected} upsellTrigger={upsellTrigger} />
    </>
  );
};

export default GenericUpsellContent;
